import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import Seo from "@/components/Seo";
import { ALL_TESTIMONIALS as TESTIMONIALS, shortName } from "@/data/testimonials";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";

const initials = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2);

export default function TestimonialsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goBook = (e) => {
    if (e) e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 600);
    setTimeout(() => scrollToId("book"), 1600);
    } else {
      scrollToId("book");
    }
  };

  return (
    <main data-testid="testimonials-page">
      <Seo
        title="Client Testimonials — ZD Design SEO & Remarketing"
        description="Real client reviews of ZD Design — SEO, ad remarketing and website optimization from Todd Meisler. Home services, real estate and consulting clients."
        path="/testimonials"
      />

      <section className="grain relative overflow-hidden pb-14 pt-40 md:pb-20 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>Proof</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              Clients who stayed —{" "}
              <span className="text-flame">and said so.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              97 recommendations across two decades —
              home services, real estate, manufacturing, consulting and
              beyond. Unedited, in their words.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div
            data-testid="testimonials-page-grid"
            className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3"
          >
            {TESTIMONIALS.map((t, i) => (
              <FadeUp key={`${t.name}-${i}`} delay={(i % 3) * 0.1} className="h-full">
                <figure
                  data-testid={`testimonial-page-card-${i + 1}`}
                  className="glass flex h-full flex-col rounded-2xl border border-slate-200 p-7 transition-all duration-500 hover:-translate-y-1 hover:border-flame/40"
                >
                  <Quote className="h-6 w-6 text-flame" />
                  <blockquote className="mt-5 flex-1 text-base leading-relaxed text-slate-800">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-7 flex items-center gap-4 border-t border-slate-200 pt-5">
                    {t.img ? (
                      <img
                        src={t.img}
                        alt={`${t.name} — ZD Design client`}
                        loading="lazy"
                        data-testid={`testimonial-page-avatar-${i + 1}`}
                        className="h-11 w-11 shrink-0 rounded-full border border-flame/40 object-cover"
                      />
                    ) : (
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-flame/40 bg-flame/10 font-mono text-base font-semibold text-flame">
                        {initials(t.name)}
                      </span>
                    )}
                    <div>
                      <div className="font-semibold text-slate-900">{shortName(t.name)}</div>
                      <div className="mt-0.5 text-base leading-snug text-faint">
                        {t.role}
                      </div>
                    </div>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <div className="glass rounded-2xl border border-slate-200 p-10 text-center md:p-14">
              <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                Ready to add your name to this wall?
              </h2>
              <p className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-mist">
                Book a free strategy call — pick a time that suits you and
                talk directly with Todd.
              </p>
              <button
                data-testid="testimonials-page-cta-book"
                onClick={goBook}
                className="group mx-auto mt-8 flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
              >
                Book a free strategy call
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
